<template>
  <li>
    <div class="row" v-if="notEditing">
      <div class="col-md-10">
        <h3>{{ endorsement.company_name }}</h3>
        <p><i class="fa fa-calendar fa-fw"></i> Submitted: {{ endorsementDateEndorsed }}</p>
        <p>{{ endorsement.description }}</p>
      </div>
      <div class="col-xs-6 col-md-2 text-right">
        <button
          @click="handleEditCallback"
          class="btn btn-sm btn-info">
          <i class="fa fa-pencil"></i> Edit
        </button>
      </div>
    </div>

    <div class="form-horizontal" v-else>
      <div class="row">
        <div class="col-sm-12">
          <div :class="$hasErrorClass($v.company_name, 'form-group')">
            <label class="control-label col-xs-12 col-sm-4 col-md-3">{{ $t('organisation') | sentenceCase }} / Client Name</label>
            <div class="col-xs-12 col-sm-8 col-md-9">
              <input
                name="company_name"
                type="text" class="form-control"
                v-model="$v.company_name.$model">
              <vuelidate-help label="Company name" :field="$v.company_name" />
            </div>
          </div>

          <div :class="$hasErrorClass($v.date_endorsed, 'form-group')">
            <label class="control-label col-xs-12 col-sm-4 col-md-3">Endorsement Date</label>

            <div class="col-xs-12 col-sm-8 col-md-9">
              <flat-pickr
                v-model="$v.date_endorsed.$model"
                :config="flatPickrConfig"
                class="form-control"
                placeholder="DD/MM/YYYY"
                name="date_endorsed">
              </flat-pickr>

              <vuelidate-help label="Endorsement date" :field="$v.date_endorsed" />
            </div>
          </div>

          <div :class="$hasErrorClass($v.description, 'form-group')">
            <label class="control-label col-xs-12 col-sm-4 col-md-3">Description</label>

            <div class="col-xs-12 col-sm-8 col-md-9">
              <textarea
                name="description"
                rows="8"
                class="form-control"
                v-model="$v.description.$model"></textarea>

              <vuelidate-help label="Description" :field="$v.description" />
            </div>
          </div>

        </div>
      </div>
      <div class="row">
        <div class="col-xs-12 col-sm-6 col-md-2 col-md-offset-8">
          <ladda-button
            class="btn btn-sm btn-success btn-block"
            :click="handleSaveCallback">
            <i class="fa fa-check"></i> Save
          </ladda-button>
        </div>
        <div class="col-xs-12 col-sm-6 col-md-2">
          <ladda-button
            class="btn btn-sm btn-danger btn-block"
            :click="handleDeleteCallback">
            <i class="fa fa-times"></i> Delete
          </ladda-button>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { pick } from 'lodash'
import {alert, notify} from 'utils'
import { helpers } from 'mixins/users/endorsements'
import FlatPickr from 'vue-flatpickr-component'
import { required } from 'vuelidate/lib/validators'
import VuelidateHelp from '../misc/VuelidateHelp'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [helpers, OpportunityTracker],

  props: ['endorsement', 'index'],

  data () {
    return {
      editing: false,
      flatPickrConfig: {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Z'
      }
    }
  },

  components: {
    VuelidateHelp,
    FlatPickr
  },

  created () {
    // If the endorsement does not initially exist then we will toggle straight to editing as they have
    // most likely just created the endorsement.
    if (this.endorsementDoesNotExist) {
      this.editing = true
    }
  },

  validations: {
    company_name: { required },
    date_endorsed: { required },
    description: { required }
  },

  computed: {
    ...mapState('user', ['user']),

    /**
     * State getter/mutator for company name.
     *
     * @return object
     */
    company_name: {
      get () {
        return this.endorsement.company_name
      },

      set (value) {
        this.setEndorsementProperty({ index: this.index, property: 'company_name', value })
      }
    },

    /**
     * State getter/mutator for date endorsed.
     *
     * @return object
     */
    date_endorsed: {
      get () {
        return this.endorsement.date_endorsed
      },

      set (value) {
        this.setEndorsementProperty({ index: this.index, property: 'date_endorsed', value })
      }
    },

    /**
     * State getter/mutator for description.
     *
     * @return object
     */
    description: {
      get () {
        return this.endorsement.description
      },

      set (value) {
        this.setEndorsementProperty({ index: this.index, property: 'description', value })
      }
    },

    /**
     * Determine if we are not currently editing the endorsement item.
     *
     * @return boolean
     */
    notEditing () {
      return !this.editing
    }
  },

  methods: {
    ...mapActions('talent/endorsement', [
      'updateTalentEndorsement',
      'storeTalentEndorsement',
      'deleteTalentEndorsement',
      'setEndorsementProperty',
      'removeEndorsement'
    ]),

    /**
     * Handle clicking the edit button.
     *
     * @return void
     */
    handleEditCallback () {
      this.editing = true
    },

    /**
     * Handle saving an existing or create a new endorsement.
     *
     * @return Promise
     */
    handleSaveCallback () {
      const toggleEditingToFalse = () => {
        this.editing = false
      }

      if (this.$v.$invalid) {
        this.$v.$touch()
        notify.error('Some fields are invalid. Please check and try again.')
        return Promise.resolve()
      }

      const payload = pick(this.endorsement, ['company_name', 'date_endorsed', 'description'])
      // If endorsement exists attempt to update.
      if (this.endorsementExists) {
        return this.updateTalentEndorsement({
          id: this.endorsement.id,
          talentId: this.user.id,
          payload
        }).then(toggleEditingToFalse)
      }
      // Otherwise attempt to create new endorsement.
      const setEndorsementsIdsInState = ({ data }) => {
        this.setEndorsementProperty({ index: this.index, property: 'id', value: data.id })
        this.setEndorsementProperty({ index: this.index, property: 'user_id', value: data.user_id })
      }

      return this.storeTalentEndorsement({
        talentId: this.user.id,
        payload
      }).then(setEndorsementsIdsInState)
        .then(toggleEditingToFalse)
        .then(() => {
          this.trackEvent(this.analytics.V2.AccountSectionEditEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.ENDORSEMENTS })
        })
    },

    /**
     * Handle deleting an endorsement from the system.
     *
     * @return Promise
     */
    handleDeleteCallback () {
      // If the endorsement does not exist we'll still return a promise except in this one
      // we'll simply remove the endorsement from the state by its index.
      if (this.endorsementDoesNotExist) {
        return new Promise((resolve) => {
          setTimeout(() => {
            this.removeEndorsement({ index: this.index })

            resolve()
          }, 500)
        })
      }

      // If the endorsement does exist we'll prompt the user to confirm they wish to delete
      // the endorsement before it is actually deleted from the system.
      return alert.confirm({
        title: 'Delete Endorsement',
        text: 'Are you sure you want to delete this endorsement?',
        buttons: {
          confirm: {
            text: 'Yes, delete endorsement.'
          }
        }
      }).then(() => {
        return this.deleteTalentEndorsement({
          id: this.endorsement.id,
          talentId: this.user.id
        })
      })
    }
  }
}
</script>
